export const MAX_PREVIEW_HTML_CACHE_ENTRIES = 100;
export const MAX_TEMPLATE_HTML_CACHE_ENTRIES = 20;

function updateRecencyOrder(
  order: string[],
  key: string,
  maxEntries: number,
): { nextOrder: string[]; evictedKeys: string[] } {
  const nextOrder = order.filter((entry) => entry !== key);
  nextOrder.push(key);
  const overflow = Math.max(0, nextOrder.length - maxEntries);
  return {
    nextOrder: overflow ? nextOrder.slice(overflow) : nextOrder,
    evictedKeys: overflow ? nextOrder.slice(0, overflow) : [],
  };
}

export function setBoundedRecordEntry<T>(
  cache: Record<string, T>,
  order: string[],
  key: string,
  value: T,
  maxEntries: number,
): string[] {
  cache[key] = value;
  const { nextOrder, evictedKeys } = updateRecencyOrder(order, key, maxEntries);
  evictedKeys.forEach((evictedKey) => delete cache[evictedKey]);
  return nextOrder;
}

export function touchBoundedRecordEntry<T>(
  cache: Record<string, T>,
  order: string[],
  key: string,
  maxEntries: number,
): string[] {
  if (!Object.prototype.hasOwnProperty.call(cache, key)) {
    return order;
  }
  const { nextOrder, evictedKeys } = updateRecencyOrder(order, key, maxEntries);
  evictedKeys.forEach((evictedKey) => delete cache[evictedKey]);
  return nextOrder;
}

export function setBoundedMapEntry<K, V>(
  cache: Map<K, V>,
  key: K,
  value: V,
  maxEntries: number,
): void {
  cache.delete(key);
  cache.set(key, value);
  while (cache.size > maxEntries) {
    const oldestKey = cache.keys().next().value as K | undefined;
    if (oldestKey === undefined) break;
    cache.delete(oldestKey);
  }
}

export function getAndTouchMapEntry<K, V>(
  cache: Map<K, V>,
  key: K,
): V | undefined {
  const value = cache.get(key);
  if (value === undefined) return undefined;
  cache.delete(key);
  cache.set(key, value);
  return value;
}
